import { createEffect, DEV, untrack } from "solid-js"
import { Group, Mesh, type Object3D } from "three"
import { parse } from "valibot"
import type { Engine } from "../../core/engine/Engine.type"
import type { EntityGraphNode } from "../../core/engine/EntityGraphNode/EntityGraphNode.type"
import applyEntityRotationToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityRotationToNextObject3DFrame"
import applyEntityWalkPathToNextObject3DFrame from "../../core/entity/EntityHeading/applyEntityWalkPathToNextObject3DFrame"
import { DegreeNumberSchema } from "../../core/entity/EntityHeading/DegreeNumber.type"
import type { MediumArtillaryEntity } from "../../core/entity/EntityType/MediumArtillary"
import shouldEntityShowFadedFrames from "../../core/entity/shouldEntityShowFadedFrames"
import tmpRng from "../../core/rng/tmpRng"
import { PI } from "../../core/util/math/Math"
import { mountOnFrameUpdateToObject3D } from "../../event/GameObject3D/GameObject3DFrameUpdate/GameObject3DFrameUpdate"
import assertClassInDev from "../../ldsh/assertClassInDev"
import { M109Paladin1_M109Paladin1_GLTF_Model } from "../gltf_models.generated"
import { appGltfLoader } from "./appGltfLoader"
import applyPlayerColors from "./applyPlayerColors"
import creaseGeometryNormalsByMeshNameList from "./creaseGeometryNormalsByMeshNameList"
import creaseMeshGeometryNormals from "./creaseMeshGeometryNormals"
import cubicEaseInOutBounce from "./cubicEaseInOutBounce"
import runWithObject3DOwner from "./runWithObject3DOwner"

const totalLoopMs = 1000

const meshesToCrease = [
  'Barrel',
  'BarrelJacket',
]

export default function makeMediumArtilleryScene(
  engine: Engine,
  ent: EntityGraphNode<MediumArtillaryEntity>,
  callback: (newGroup: Object3D) => void
): void {
  return appGltfLoader.load(M109Paladin1_M109Paladin1_GLTF_Model, function (ModelObj) {
    tmpRng.seed = ent.id
    const { scene } = ModelObj
    // printObjectHierarchy(scene)

    const bodyGroup = scene.getObjectByName('Body') as Group
    assertClassInDev(bodyGroup, Group)

    const turretGroup = scene.getObjectByName('Turret') as Group
    assertClassInDev(turretGroup, Group)

    const barrelBaseMesh = scene.getObjectByName('BarrelBase') as Mesh
    assertClassInDev(barrelBaseMesh, Mesh)

    // turretGroup.position.y -= 2
    // turretGroup.position.y = 2.5
    // barrelGroup.position.y = 0.7
    // barrelGroup.position.z = 5
    barrelBaseMesh.rotateX(-PI / 8)

    const leftTrackMesh = scene.getObjectByName('LeftTrack') as Mesh
    creaseMeshGeometryNormals(leftTrackMesh)
    assertClassInDev(leftTrackMesh, Mesh)

    const rightTrackMesh = leftTrackMesh.clone()
    rightTrackMesh.name = 'RightTrack'
    scene.add(rightTrackMesh)

    rightTrackMesh.position.x = 9

    applyPlayerColors(engine, scene, ent)

    const defaultHullY = bodyGroup.position.y
    // const defaultHullX = 0

    let entValue = untrack(ent.raw)
    runWithObject3DOwner(scene, () => {
      createEffect(() => {
        entValue = ent.raw()
      })
    })


    mountOnFrameUpdateToObject3D(engine, bodyGroup, (elapsedMs: number, frameAt: number): void => {
      const mod = frameAt % totalLoopMs
      if (shouldEntityShowFadedFrames(entValue)) {
        bodyGroup.position.y = defaultHullY
      } else if (entValue.player_id) {
        bodyGroup.position.y = defaultHullY + (cubicEaseInOutBounce(mod / 500) / 7)
      }
      applyEntityRotationToNextObject3DFrame(scene, elapsedMs)
      applyEntityWalkPathToNextObject3DFrame(scene, elapsedMs)
    })

    turretGroup.userData.e = ent

    if (DEV) {
      parse(DegreeNumberSchema, entValue.heading)
    }

    // turretGroup.userData.targetHeading = entValue.heading
    // turretGroup.userData.heading = entValue.heading
    turretGroup.userData.targetHeading = 0
    turretGroup.userData.heading = 0
    mountOnFrameUpdateToObject3D(engine, turretGroup, (elapsedMs: number): void => {
      applyEntityRotationToNextObject3DFrame(turretGroup, elapsedMs)
    })

    creaseGeometryNormalsByMeshNameList(scene, meshesToCrease)

    callback(scene)
  })
}
